import React from "react";
import { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { TranslationKeys } from "./utils";
import type { TruckHistory } from "../../../types/truckHistory";
import {
  fetchTruckHistories,
  deleteTruckHistory,
  selectTruckHistoryDeleting,
} from "../../../store/features/truckHistorySlice";
import type { AppDispatch, RootState } from "../../../store";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { PaginationResponse } from "../../../types/paginationResponse";
import TablePagination from "../../common/TablePagination";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { selectCurrentPage, selectPageMetadata } from "../../../store/features/appStateSlice";
import { rawColor } from "../../../constants/colors";
import HistoryEntryRow from "./HistoryEntryRow";
import { isUserAdmin } from "../../../types/user";

const TRUCK_HISTORY_PAGE_SIZE_KEY = "truckHistoryPageSize";
const DEFAULT_PAGE_SIZE = 5;

interface TruckHistoryTableProps {
  history: TruckHistory[];
  loading: boolean;
  getCityName: (cityId: string) => string;
  truckId: string;
  isExpanded: boolean;
  pagination?: PaginationResponse;
}

const TruckHistoryTable: React.FC<TruckHistoryTableProps> = ({
  history,
  loading,
  getCityName,
  truckId,
  isExpanded,
  pagination,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const currentPage = useSelector(selectCurrentPage);
  const pageMetadata = useSelector(selectPageMetadata);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [paginationParam, setPaginationParam] = useState(() => {
    const savedPageSize = localStorage.getItem(TRUCK_HISTORY_PAGE_SIZE_KEY);
    return {
      page: 1,
      pageSize: savedPageSize ? Number.parseInt(savedPageSize, 10) : DEFAULT_PAGE_SIZE,
    };
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Get deleting states for all entries
  const deletingStates = useSelector((state: RootState) =>
    Object.fromEntries(
      history.map((entry) => [entry._id, selectTruckHistoryDeleting(state, entry._id)]),
    ),
  );

  // Simplify grouping to only handle visual organization
  const groupedHistory = useMemo(() => {
    const groups: Array<{
      id: string;
      entries: TruckHistory[];
      isDeleted: boolean;
    }> = [];

    let currentGroup: TruckHistory[] = [];
    let currentGroupId = "";

    history.forEach((entry, index) => {
      if (entry.isDeleted) {
        if (currentGroup.length === 0) {
          currentGroupId = `deleted-${index}`;
        }
        currentGroup.push(entry);
      } else {
        if (currentGroup.length > 0) {
          groups.push({
            id: currentGroupId,
            entries: [...currentGroup],
            isDeleted: true,
          });
          currentGroup = [];
        }
        groups.push({
          id: entry._id,
          entries: [entry],
          isDeleted: false,
        });
      }
    });

    if (currentGroup.length > 0) {
      groups.push({
        id: currentGroupId,
        entries: [...currentGroup],
        isDeleted: true,
      });
    }

    return groups;
  }, [history]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(
        fetchTruckHistories({
          truckId,
          ...paginationParam,
        }),
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePaginationChange = (newPagination: {
    page: number;
    pageSize: number;
  }) => {
    setPaginationParam(newPagination);
    localStorage.setItem(TRUCK_HISTORY_PAGE_SIZE_KEY, newPagination.pageSize.toString());
    dispatch(
      fetchTruckHistories({
        truckId,
        ...newPagination,
      }),
    );
  };

  const handleDelete = async (historyId: string) => {
    try {
      await dispatch(deleteTruckHistory(historyId)).unwrap();
      setDeleteDialogOpen(null);
    } catch (error) {
      setDeleteDialogOpen(null);
    }
  };

  const canEdit = (entry: TruckHistory) => {
    const isOlderThanOneDay =
      new Date().getTime() - new Date(entry.timestamp).getTime() > 24 * 60 * 60 * 1000;
    return (entry.user._id === user?._id && !isOlderThanOneDay) || isUserAdmin(user);
  };

  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  useEffect(() => {
    if (isExpanded && !history.length) {
      dispatch(
        fetchTruckHistories({
          truckId,
          ...paginationParam,
        }),
      );
    }
  }, [isExpanded, history.length, truckId, dispatch, paginationParam]);

  return (
    <>
      <TableContainer sx={{ width: "100%" }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell width="15%" sx={{ fontWeight: "bold" }}>
                {t("Timestamp" as TranslationKeys)}
              </TableCell>
              <TableCell width="120px" sx={{ fontWeight: "bold" }}>
                {t("Status" as TranslationKeys)}
              </TableCell>
              <TableCell width="20%" sx={{ fontWeight: "bold" }}>
                {t("Location" as TranslationKeys)}
              </TableCell>
              <TableCell width="20%" sx={{ fontWeight: "bold" }}>
                {t("Destination" as TranslationKeys)}
              </TableCell>
              <TableCell width="20%" sx={{ fontWeight: "bold" }}>
                {t("Notes" as TranslationKeys)}
              </TableCell>
              <TableCell width="10%" sx={{ fontWeight: "bold" }}>
                {t("Created by" as TranslationKeys)}
              </TableCell>
              <TableCell width="5%" sx={{ fontWeight: "bold" }}>
                {""}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || isRefreshing ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (
              groupedHistory.map((group) => (
                <React.Fragment key={group.id}>
                  {group.isDeleted ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0 }}>
                        <Box
                          sx={(theme) => ({
                            backgroundImage: `repeating-linear-gradient(
                              45deg,
                              ${
                                theme.palette.mode === "dark"
                                  ? rawColor.white.transparent["01"]
                                  : rawColor.black.transparent["03"]
                              },
                              ${
                                theme.palette.mode === "dark"
                                  ? rawColor.white.transparent["01"]
                                  : rawColor.black.transparent["03"]
                              } 10px,
                              ${
                                theme.palette.mode === "dark"
                                  ? rawColor.white.transparent["02"]
                                  : rawColor.black.transparent["05"]
                              } 10px,
                              ${
                                theme.palette.mode === "dark"
                                  ? rawColor.white.transparent["02"]
                                  : rawColor.black.transparent["05"]
                              } 20px
                            )`,
                            borderRadius: 1,
                            width: "100%",
                          })}
                        >
                          <Table size="small" sx={{ width: "100%", background: "none" }}>
                            <TableBody>
                              <TableRow
                                sx={{
                                  "& td": {
                                    borderBottom: "none",
                                    backgroundColor: "transparent",
                                    py: 0.5,
                                    color: "text.secondary",
                                    fontSize: "0.875rem",
                                  },
                                  "& .MuiBox-root": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                                onClick={() => toggleGroupExpand(group.id)}
                              >
                                <TableCell
                                  colSpan={7}
                                  sx={{
                                    color: "text.secondary",
                                    borderBottom: "none",
                                    backgroundColor: "transparent",
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {expandedGroups[group.id] ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                      {t("Deleted Entries" as TranslationKeys)} (
                                      {group.entries.length})
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {expandedGroups[group.id] &&
                                group.entries.map((entry) => (
                                  <HistoryEntryRow
                                    key={entry._id}
                                    entry={entry}
                                    getCityName={getCityName}
                                    onDelete={setDeleteDialogOpen}
                                    canEdit={canEdit}
                                    isDeleting={deletingStates[entry._id]}
                                    isExpanded={true}
                                    i18n={i18n}
                                    t={t}
                                  />
                                ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      <HistoryEntryRow
                        entry={group.entries[0]}
                        getCityName={getCityName}
                        onDelete={setDeleteDialogOpen}
                        canEdit={canEdit}
                        isDeleting={deletingStates[group.entries[0]._id]}
                        i18n={i18n}
                        t={t}
                      />
                      {expandedGroups[group.id] && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0 }}>
                            <Table size="small">
                              <TableBody>
                                {group.entries.map((entry) => (
                                  <HistoryEntryRow
                                    key={entry._id}
                                    entry={entry}
                                    getCityName={getCityName}
                                    onDelete={setDeleteDialogOpen}
                                    canEdit={canEdit}
                                    isDeleting={deletingStates[entry._id]}
                                    isExpanded={true}
                                    i18n={i18n}
                                    t={t}
                                  />
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        count={pagination?.total || 0}
        pagination={paginationParam}
        onPaginationChange={handlePaginationChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
        showRowsPerPage={true}
      />
      <ConfirmationDialog
        open={!!deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(null)}
        onConfirm={() => deleteDialogOpen && handleDelete(deleteDialogOpen)}
        title={t("Delete History Entry" as TranslationKeys)}
        message={t(
          "Are you sure you want to delete this history entry? This action cannot be undone. You may delete history if you made a mistake while creating it." as TranslationKeys,
        )}
        confirmButtonText={t("Delete" as TranslationKeys)}
      />
    </>
  );
};

export default TruckHistoryTable;
